import { useCallback, useMemo, useState } from 'react';
import {
  isGoogleCalendarNotConfiguredError,
  useGetSchedulingConnectionsQuery,
  useRevokeSchedulingConnectionMutation,
  useStartGoogleCalendarOAuthMutation,
  useUpdateSchedulingConnectionCalendarsMutation,
} from '../endpoints/scheduling.endpoints';
import type { SchedulingConnection } from '../types/scheduling.types';

export function useSchedulingConnections() {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetSchedulingConnectionsQuery();

  const [startOAuth, { isLoading: isStartingOAuth }] = useStartGoogleCalendarOAuthMutation();
  const [updateCalendars, { isLoading: isSavingCalendars }] = useUpdateSchedulingConnectionCalendarsMutation();
  const [revokeConnection, { isLoading: isRevoking }] = useRevokeSchedulingConnectionMutation();

  const [optimisticConnections, setOptimisticConnections] = useState<Record<string, SchedulingConnection>>({});

  const connections = useMemo(() => {
    const results = data?.results ?? [];
    return results.map((connection) => optimisticConnections[connection.id] ?? connection);
  }, [data?.results, optimisticConnections]);

  const isGoogleCalendarNotConfigured = isError && isGoogleCalendarNotConfiguredError(error);

  const connectCalendar = useCallback(async() => {
    try {
      const result = await startOAuth().unwrap();
      window.location.assign(result.authorization_url);
      return { ok: true as const };
    } catch (mutationError: unknown) {
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string'
        ? responseData.detail
        : 'Could not start Google Calendar connection.';
      return { ok: false as const, message: detail };
    }
  }, [startOAuth]);

  const saveCalendarSelection = useCallback(async(connectionId: string, calendarIds: string[]) => {
    try {
      const updated = await updateCalendars({ connectionId, calendarIds }).unwrap();
      setOptimisticConnections((current) => ({ ...current, [connectionId]: updated }));
      return { ok: true as const };
    } catch (mutationError: unknown) {
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string'
        ? responseData.detail
        : 'Could not save calendar selection.';
      return { ok: false as const, message: detail };
    }
  }, [updateCalendars]);

  const revokeConnectionAccess = useCallback(async(connectionId: string) => {
    const previous = connections.find((connection) => connection.id === connectionId);
    if (!previous) {
      return;
    }

    const optimistic: SchedulingConnection = {
      ...previous,
      status: 'revoked',
      is_active: false,
    };
    setOptimisticConnections((current) => ({ ...current, [connectionId]: optimistic }));

    try {
      const updated = await revokeConnection(connectionId).unwrap();
      setOptimisticConnections((current) => ({ ...current, [connectionId]: updated }));
    } catch {
      setOptimisticConnections((current) => {
        const next = { ...current };
        delete next[connectionId];
        return next;
      });
    }
  }, [connections, revokeConnection]);

  return {
    connections,
    isLoading: isLoading || isFetching,
    isError,
    isGoogleCalendarNotConfigured,
    error,
    refetch,
    connectCalendar,
    isStartingOAuth,
    saveCalendarSelection,
    isSavingCalendars,
    revokeConnectionAccess,
    isRevoking,
    actionsDisabled: isGoogleCalendarNotConfigured,
  };
}
